// Hand-authored room templates on the 48 px tile grid (Phaser-free data).
//
// Legend (binding, see docs/ARCHITECTURE.md):
//   #  solid          =  one-way platform     ^  hazard (solid, hurts the player)
//   .  empty          b  decorative back wall (non-colliding)
//   P  player spawn   X  exit gate            g  ground spawn point   a  air spawn point
//   O  orb spot       S  service station      R  repair bay           C  crate
//   T  challenge terminal                     B  boss anchor (centre-bottom of the boss)
// Ground markers (P X g O S R C T B) sit in the cell directly above the surface they stand
// on. A horizontal run of a wide-prop letter (BB, XX…) anchors at the run's centre.
//
// Traversal budget (the heaviest frame, BASTION, sets the limits): single jump ≈ 3.2 tiles,
// double jump ≈ 5.7, VOLT/WRAITH single ≈ 3.9. Required climbs rise ≤ 4 tiles (usually 3),
// gaps ≤ 4 tiles, pits are ≤ 3 deep so a hazard bounce + jump always escapes.
// tests/route-rooms.test.ts verifies shape, markers, reachability and softlock freedom.

import type { NodeType } from '../core/types';

export interface RoomTemplate {
  id: string;
  type: NodeType;
  name: string;
  tiles: string[];
}

/** Indented template literal → rows (surrounding blank lines and indentation removed). */
function grid(src: string): string[] {
  return src
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
}

export const ROOMS: RoomTemplate[] = [
  {
    id: 'start_drop_zone',
    type: 'start',
    name: 'DROP ZONE',
    tiles: grid(`
      ###..................................###
      ###..................................###
      ###..................................###
      ###..................................###
      ###..................................###
      #...................a................###
      #....................................###
      #.............bbb............a.........#
      #...........a.bbb.....bbbbbbb..........#
      #..........bbbbbbbb...bbbbbbb..........#
      #..........bbbbbbbb...bbabbbb..........#
      #..........bbbbbbbbbbbbbbbbbb..........#
      #..........bbbbbbbbbbbbbbbbbb..........#
      #..........bbbbbbbbgbbbbbbbbb..........#
      #..........bbbbb========bbbbb..........#
      #...P..C...bbbbbbbbbbbbbbbbbb......X...#
      ########...bbbgbbbbbbbbbbgbbb...########
      ########...b======bbbb======b...########
      ##########.bbbbbbbbbbbbbbbbbb.##########
      ##########.bbgbbbbbbCbbbbbgbb.##########
      ########################################
      ########################################
      ########################################
    `),
  },
  {
    id: 'start_breach',
    type: 'start',
    name: 'BREACH POINT',
    tiles: grid(`
      ############################################
      ############################################
      ##############bbbbbbbbbb##bbbbbbbbbb###bbbb#
      ##############bbbbbbbbbb##bbbbbbbbbb###bbbb#
      ##############bbbbbbbbbb##bbbbbbbbbb###bbbb#
      ##############bbbbbbbbbb##bbbbbbbbbbbbbbbbb#
      ###########bbbbbbbbbbbbb##bbbbbbbbbbbbbbbbb#
      ###########bbbbbbbbbbbbbbbbbbbbbbabbbbbbbbb#
      ###########bbbbbbbabbbbbbbbbbbbbbbbbbbbbbbb#
      ###########bbbbbbbbbbbbbbbbbabbbbbbbbbbbbbb#
      ###########bbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbb#
      ###########bbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbb#
      ###########bbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbb#
      #..........bbbbbbbb=======bbbbbbbbbbbbbgbbb#
      #..........bbbbbbbbbbbbbbbbbbbbbbbb#########
      #..........bbbbgbbbbbbbbbbbbbbbgbbb#########
      #..........bb=====bbbbbbbbbb======bbbbbbbbb#
      ...........bbbbbbbbbbbgbbbbbbbbbbb====bbbbb#
      ...........bbbbbbbb#######bbbbbbbbbbbbbbbbb#
      ...P...C.C.bbbbgbbb#######bbbgbCbbbbbbbXbbb#
      ############################################
      ############################################
      ############################################
    `),
  },
  {
    id: 'combat_yard',
    type: 'combat',
    name: 'PLATFORM YARD',
    tiles: grid(`
      #..............................................#
      #..............................................#
      #..............................................#
      #..............................................#
      ###............................................#
      ###.....................a......................#
      ###..........................................###
      ###.......a..........................a.......###
      ###...............................bbbbbbb....###
      ###...............a...........a...bbbbbbb....###
      ###....bbbbbbb.........g..........bbbbbbb....###
      ###....bbbbbbb.......======.......bbbbbbb....###
      ###....bbbbbbb....bbbbbbbbbbbb....bbbbbbb....###
      ###....bbbbbbbg...bbbbbbbbbbbb...gbbbbbbb....###
      ###....bbbbb======bbbbbbbbbbbb======bbbbb....###
      ###....bbbbbbb....bbbbbbbbbbbb....bbbbbbb....###
      ###....gbbbbbb....bbbbbgbbbbbb....bbbbbbg....###
      ###..======bbb..C.bbb======bbb.C..bbb======..###
      ###....bbbbbbb.##.bbbbbbbbbbbb.##.bbbbbbb....###
      ###.P..bgbbbbb.##.bgbbbbTbbbgb.##.bbgbbbb.X..###
      ################################################
      ################################################
      ################################################
    `),
  },
  {
    id: 'combat_spires',
    type: 'combat',
    name: 'TWIN SPIRES',
    tiles: grid(`
      #..................................................#
      #..................................................#
      #..................................................#
      #..................................................#
      #........................a.........................#
      #.......a...................................a......#
      #...............g..................g...............#
      #..............###................###..............#
      #..............###....g......g....###..............#
      #.............#####==============#####.............#
      #.............#####..............#####....bbbbb....#
      #........=====#####..............#####=====bbbb....#
      #....bbbbb....#####.a...====...a.#####....bbbbb....#
      #....bbbbb....#####..............#####....bbbbb....#
      #....bbbbb.===#####...bbbbbbbb...#####===.bbbbb....#
      #....bbbbb....#####===bbbbgbbb===#####....bbbbb....#
      #....bbbbb.g..#####...b======b...#####..g.bbbbb....#
      #....bbbb=====#####...bbbbbbbb...#####=====bbbb....#
      #....bbbbb....#####...bbbbbbbb...#####....bbbbb....#
      #..P.bbTbbg...#####..CbbbgbbbbC..#####...gbbbbbX...#
      ####################################################
      ####################################################
      ####################################################
    `),
  },
  {
    id: 'combat_sump',
    type: 'combat',
    name: 'THE SUMP',
    tiles: grid(`
      ################................################
      ################................################
      ###############..................###############
      ###############..................###############
      #bbbbbbbbbbb........................bbbbbbbbbbb#
      #bbbbbbbbbbb............a...........bbbbbbbbbbb#
      #bbbbbbbbbbb........................bbbbbbbbbbb#
      #bbbbbbbbbbb.....a.............a....bbbbbbbbbbb#
      #bbbbbbbbbbb........................bbbbbbbbbbb#
      #bbbbbbbbbbb........................bbbbbbbbbbb#
      #bbbbbbbbbbb...........g............bbbbbbbbbbb#
      #bbbbbbbbbbb........========........bbbbbbbbbbb#
      #bbbbbbbbbbb........................bbbbbbbbbbb#
      #bbbbbbbbbbg.......................gbbbbbbbbbbb#
      #bbbbbbb=========..............=========bbbbbbb#
      #bbbbbbbbbbb............a...........bbbbbbbbbbb#
      #bbbbbbbbbbb........................bbbbbbbbbbb#
      #bbPbbTbgCbb...g....g......g........bbCbgbbbXbb#
      ############..===..##..==..##..===..############
      ############.......##......##.......############
      ############.......##......##.......############
      ############^^^^^^^^^^^^^^^^^^^^^^^^############
      ################################################
    `),
  },
  {
    id: 'combat_shaft',
    type: 'combat',
    name: 'RELAY SHAFT',
    tiles: grid(`
      ########################################
      ########################################
      ##bbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbb##
      ##bbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbb##
      ##bbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbb##
      ##bbbbbbabbbbbbbbbbbbbbbbbbbbbbbabbbbb##
      ##bbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbb##
      ##bbbbbbbbbbbbbbbbbbXbbbbbbbbbbbbbbbbb##
      ##bbbbbbbb####################bbbbbbbb##
      ##bbbbbbbb####################bbbbbbbb##
      ##bbbgbbbbbbbbbbbbbbbbbbbbbbbbbbbbgbbb##
      ##=======bbbbbbbbbbbbbbbbbbbbbb=======##
      ##bbbbbbbbbbbbbbbbbbabbbbbbbbbbbbbbbbb##
      ##bbbbbbbbbbgbbbbbbbbbbbbbbgbbbbbbbbbb##
      ##bbbbbbbb=======bbbbbb=======bbbbbbbb##
      ##bbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbb##
      ##bbbbbbbbabbbbbbbbbgbbbbbbbbabbbbbbbb##
      ##bbbbbbbbbbbbbbbb####bbbbbbbbbbbbbbbb##
      ##bbbbbbbbbbbbbbbb####bbbbbbbbbbbbbbbb##
      ##bbbbbbbbbb======####======bbbbbbbbbb##
      ##bbbbbbbbbbbbbbbb####bbbbbbbbbbbbbbbb##
      ##bbgbbbbbbbbbbbbb####bbbbbbbbbbbbbgbb##
      ##======bbbbbbbbbb####bbbbbbbbbb======##
      ##bbbbbbbbbbbbbbbb####bbbbbbbbbbbbbbbb##
      ##bbbbbbbbbbbbgbbb####bbbgbbbbbbbbbbbb##
      ##bbbbbbbabb======####======bbabbbbbbb##
      ##bbbbbbbbbbbbbbbb####bbbbbbbbbbbbbbbb##
      ##bbgbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbgbb##
      ##======bbbbbbbbbbbbbbbbbbbbbbbb======##
      ##bbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbb##
      ##bbbPbbbbgbbbCbbbbbTbbbbbCbbgbbbbbbbb##
      ########################################
      ########################################
      ########################################
    `),
  },
  {
    id: 'combat_catwalks',
    type: 'combat',
    name: 'CATWALK TIERS',
    tiles: grid(`
      ########################################################
      ########################################################
      #bbbbbbbbb##bbbbbbbbbbbbbb####bbbbbbbbbbbbbb##bbbbbbbbb#
      #bbbbbbbbb##bbbbbbbbbbbbbb####bbbbbbbbbbbbbb##bbbbbbbbb#
      #bbbbbbbbbbbbbbbbbbbbbbbbb####bbbbbbbbbbbbbbbbbbbbbbbbb#
      #bbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbb#
      #bbbbbbbbbbbbabbbbbbbbbbbbbbabbbbbbbbbbbbbabbbbbbbbbbbb#
      #..........................g...........................#
      #....................==============....................#
      #......................................................#
      #....g...............g.............g..............X....#
      #.========.....============....==========.....========.#
      #......................................................#
      #............g..........................g..............#
      #.......============...............=============.......#
      #.......................a.........................a....#
      #.........g........................g.........g.........#
      #..============...======...C....======...============..#
      #........................#####.........................#
      #.P.....g........T...g...#####...g...C........g........#
      ########################################################
      ########################################################
      ########################################################
    `),
  },
  {
    id: 'combat_pillar',
    type: 'combat',
    name: 'PILLAR COURT',
    tiles: grid(`
      #..........................................#
      #..........................................#
      #..........................................#
      #..........................................#
      #.....................a....................#
      #..........................................#
      #..........................................#
      #.......a..........................a.......#
      #....................g.....................#
      #............a...##########...a............#
      #................##########................#
      #...bbbbb...........####...........bbbbb...#
      #...bbbbb....====...####...====....bbbbb...#
      #...bbbbb...g.......####.......g...bbbbb...#
      #...bbbbb.======....####....======.bbbbb...#
      #...bbbbb...........####...........bbbbb...#
      #...bbgbb.C.........####.........C.bbgbb...#
      #...=====##.........####.........##=====...#
      #...bbbbb##.........####.........##bbbbb...#
      #.P.bbgbb##.g.T...g.####.g....g..##bbgbbX..#
      ############################################
      ############################################
      ############################################
    `),
  },
  {
    id: 'combat_skyway',
    type: 'combat',
    name: 'BROKEN SKYWAY',
    tiles: grid(`
      #..........................................................#
      #..........................................................#
      #..........................................................#
      #..........................................................#
      #..........................................................#
      #..................................a.......................#
      #................a.........................................#
      #.....................................................a....#
      #..........................................................#
      #...............a..........................................#
      #..........................................................#
      #.P......T.g..........g.C......g.........g.....C..g........#
      ###############....###############....###############......#
      ###############....###############....###############......#
      #...bb##bb.............bb##bb.............bb##bb...........#
      #...bb##bb.....===.....bb##bb.....===.....bb##bb.....===...#
      #...bb##bb.............bb##ba.............bb##bb...........#
      #...bb##bb..g..====....bb##bb.....====....bb##===..........#
      #...bb##bb.##..........bb##bb.g...........bb##bb.##........#
      #...bb##bb.##......g...bb##bb###.........gbb##bb.##..g..X..#
      ##################################^^^^^#####################
      ############################################################
      ############################################################
    `),
  },
  {
    id: 'combat_scaffold',
    type: 'combat',
    name: 'SCAFFOLD WARREN',
    tiles: grid(`
      ################################################
      ################################################
      #bbbbbbb##bbbbbbbbbb##bbbbbbbb##bbbbbbb##bbbbbb#
      #bbbbbbb##bbbbbbbbbb##bbbbbbbb##bbbbbbb##bbbbbb#
      #bbbbbbb##bbbbbbbbbb##bbbbbbbb##bbbbbbb##bbbbbb#
      #bbbbbbb##bbabbbbbbb##bbbabbbb##bbbabbb##bbbbbb#
      #bbbbbbb##bbbbbbbbbb##bbbbbbbb##bbbbbbb##bbbbbb#
      #bbbbbbbbbbbbbgbbbbb##bbbbgbbbbbbbbbbbb##bbbbbb#
      #bbbbbbbbb========bbbb========bbb======bbbbbbbb#
      #bbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbb#
      #bbbbbgbbbbbbbbbbgbbbbbbbbbbgbbbbbbbbbbbgbbbbbb#
      #bb========bbb=======bbbb========bbbb========bb#
      #bbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbb#
      #bbbbbbbbbbgbbbbbbbbbbbbgbbbbbbbbbbbgbbbbbbbbbb#
      #bbbbbbb========bbb==========bbb========bbbbbbb#
      #bbbbbbbbbbbbbbbabbbbbbbbbbbbbbbbabbbbbbbbbbbbb#
      #bbbbbgbbbbbbbbbbbgbbbbbbbbbbgbbbbbbbbbbbgbbbbb#
      #bbb======bbbb========bbCb========bbCb=======bb#
      #bbbbbbbbbb##bbbbbbbbbb##bbbbbbbbbb##bbbbbbbbbb#
      #bPbbbbgbbb##bbbbgbbbbb##bbbbTgbbbb##bbbbgbbbXb#
      ################################################
      ################################################
      ################################################
    `),
  },
  {
    id: 'combat_hall',
    type: 'combat',
    name: 'OPEN HALL',
    tiles: grid(`
      ####################################################
      ####################################################
      ####################################################
      #bbbbbbb####bbbbbbbbbb########bbbbbbbbbb####bbbbbbb#
      #bbbbbbb####.......bbbbb####bbbbb.......####bbbbbbb#
      #bbbbbbb...........bbbbbbbbbbbbbb...........bbbbbbb#
      #bbbbbbb...........bbbbbbabbbbbbb...........bbbbbbb#
      #bbbbbbb.....a.....bbbbbbbbbbbbbb.....a.....bbbbbbb#
      #bbbbbbb...........bbbbbbbbbbbbbb...........bbbbbbb#
      #bbbbbbb...........abbbbbbbbbbbba...........bbbbbbb#
      #bbbbbba...........bbbbbbgbbbbbbb...........babbbbb#
      #bbbbbbb...........bbb========bbb...........bbbbbbb#
      #bbbbbbb...........bbbbbbbbbbbbbb...........bbbbbbb#
      #bbgbbbbbbbbbbbbgbbbbbbbbbbbbbbbbbbgbbbbbbbbbbbbgbb#
      ######bbbbbbbb======bbbbbbbbbbbb======bbbbbbbb######
      ######bbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbb######
      #bbbbbbbbbgbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbgbbbbbbbbb#
      #bbbbbbb======bbbbbbbbbbbgbbbbbbbbbbbb======bbbbbbb#
      #bbbbbbbbbbbbbbbbbbb############bbbbbbbbbbbbbbbbbbb#
      #bbPbbbgbbTbbbbCgb################bgbCbbbbbbgbbbXbb#
      ####################################################
      ####################################################
      ####################################################
    `),
  },
  {
    id: 'elite_crucible',
    type: 'elite',
    name: 'THE CRUCIBLE',
    tiles: grid(`
      ###############..................###############
      ###############..................###############
      #bbbbbbbbbbbbbb..................bbbbbbbbbbbbbb#
      #bbbbbbbbbbbbbb..................bbbbbbbbbbbbbb#
      #bbbbbbbbbbbbbb.........a........bbbbbbbbbbbbbb#
      #bbbbbbbbbbbbbb..................bbbbbbbbbbbbbb#
      #bbbbbbbbbbbbbb.a..............a.bbbbbbbbbbbbbb#
      #bbbbbbbbbbbbbb..................bbbbbbbbbbbbbb#
      #bbbbbbbbbbbbbb..................bbbbbbbbbbbbbb#
      #bbgbbbbbbbabbb..................bbbabbbbbbbgbb#
      ######bbbbbbbbb........g.........bbbbbbbbb######
      ######bbbbbbbbb....==========....bbbbbbbbb######
      #bbbbb====bbbbb..................bbbbb====bbbbb#
      #bbbbbbbbbbbbbbg................gbbbbbbbbbbbbbb#
      #bbbbbbbbbbbb======..........======bbbbbbbbbbbb#
      #b======bbbbbbb..................bbbbbbb======b#
      #bbbbbbbbbbbbbb..................bbbbbbbbbbbbbb#
      #bbPbbbTgbbbbbb...g.........g....bbbbbbgCbbbXbb#
      ############....======....======....############
      ############........................############
      ##########.......g.....g......g.......##########
      ############^^####################^^############
      ################################################
    `),
  },
  {
    id: 'elite_overpass',
    type: 'elite',
    name: 'OVERPASS',
    tiles: grid(`
      #..................................................#
      #..................................................#
      #..................................................#
      #..................................................#
      #..................................................#
      #.........................a........................#
      #.............a.......................a............#
      #..................................................#
      ##................................................##
      ###..............................................###
      ###...g............a.....g.......a...........g...###
      ###.======............========............======.###
      ###..............................................###
      ###.........g.............g............g.........###
      ###.....====================================.....###
      ###...........bb.........bb.........bb...........###
      ###..g........bb.........bb.........bb........g..###
      ###======.....bb.......======.......bb.....======###
      ###...........bb.........bb.........bb...........###
      ###.P..g..T...bb.........gbC........bb....g...X..###
      ##################^^^^########^^^^##################
      ####################################################
      ####################################################
    `),
  },
  {
    id: 'loot_vault',
    type: 'loot',
    name: 'SUPPLY VAULT',
    tiles: grid(`
      ############################################
      ############################################
      ############################################
      ##bbb#######bbbbbbbbbbbbbbbbbbbb#######bbb##
      ##bbb#######bbbbbbbbbbbbbbbbbbbb#######bbb##
      ##bbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbb##
      ##bbbbbbbbbbbbbbbbbbbbabbbbbbbbbbbbbbbbbbb##
      ##bbbbbbbbbbbbbabbbbbbbbbbbbbabbbbbbbbbbbb##
      ##bbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbb##
      ##bbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbb##
      ##bbbbbbbbbbbbbgbbbbbabbbbbbgbbbbbbbbbbbbb##
      ##bbbbbbbbbbb======bbbbbb======bbbbbbbbbbb##
      ##bbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbb##
      ##bbbbbbbbbbbbbbbbbbbgbbbbbbbbbbbbbbbbbbbb##
      ##bbbbbbObbbbbbbbbb======bbbbbbbbbbObbbbbb##
      ##bbbb#####bbbbbbbbbbbbbbbbbbbbbb#####bbbb##
      ##bbbb#####bbbbbbbgbbbbbbgbbbbbbb#####bbbb##
      ..bbbbbbbbb===bbb==========bbb===bbbbbbbbb##
      ..bbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbb##
      ..bPbbbbbbbbbgbCbbbbbgbbbbbbCbgbbbbbbbbbXb##
      ############################################
      ############################################
      ############################################
    `),
  },
  {
    id: 'loot_spires',
    type: 'loot',
    name: 'CACHE SPIRES',
    tiles: grid(`
      #..........................................#
      #..........................................#
      #..........................................#
      #..........................................#
      #..........................................#
      #..........................................#
      #.....a..............a...............a.....#
      #..........O....................O..........#
      #........#####................#####........#
      #.........###....a........a....###.........#
      #.........###.g................###.........#
      #.....====###===............===###====.....#
      #.........###..................###.........#
      #..bgbbb..###........g.........###..bbbgb..#
      #.=====b..###.....========.....###..b=====.#
      #..bbbbb..###......bbbbbb......###..bbbbb..#
      #..bbbbg..###......bbbbbb......###..gbbbb..#
      #..bb=====###====..bbbbbb..====###=====bb..#
      #..bbbbb..###......bbbbbb......###..bbbbb..#
      #..Xbbbg..###..gC..bbPbbb..Cg..###..bbgbb..#
      ############################################
      ############################################
      ############################################
    `),
  },
  {
    id: 'repair_bay',
    type: 'repair',
    name: 'REPAIR BAY',
    tiles: grid(`
      ########################################
      ########################################
      ########################################
      #bbbbbbbbbbbbbb##########bbbbbbbbbbbbbb#
      #bbbbbbbbbbbbbb##########bbbbbbbbbbbbbb#
      #bbbbbbbbbbbbbbbbb####bbbbbbbbbbbbbbbbb#
      #bbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbb#
      #bbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbb#
      #bbbbbbbbbbbabbbbbbbbbbbbbbbabbbbbbbbbb#
      #bbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbb#
      #bbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbb#
      #bbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbb#
      #bbbbbbbbbbb======bbbb======bbbbbbbbbbb#
      #bbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbb#
      #bbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbb#
      #bbbbbgbbbbbbbbbbbbbbbbbbbbbbbbbbgbbbbb#
      #bbb======bbbbbbbbbbbbbbbbbbbb======bbb#
      .bbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbb#
      .bbbbbbb##bbbbbbbbbbbbbbbbbbbb##bbbbbbb#
      .bbPbbCb##bgCbbbbbbbRbbbbbbCgb##bCbbXbb#
      ########################################
      ########################################
      ########################################
    `),
  },
  {
    id: 'station_depot',
    type: 'station',
    name: 'SERVICE DEPOT',
    tiles: grid(`
      ############################################
      ############################################
      ############################################
      #bbbbbbbbbbbbbbbb##########bbbbbbbbbbbbbbbb#
      #bbbbbbbbbbbbbbbb##########bbbbbbbbbbbbbbbb#
      #bbbbbbbbbbbbbbbb##########bbbbbbbbbbbbbbbb#
      #bbbbbbbbbbbbbbbbbb######bbbbbbbbbbbbbbbbbb#
      #bbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbb#
      #bbbbbbbbbbbabbbbbbbbbbbbbbbbbbabbbbbbbbbbb#
      #bbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbb#
      #bbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbb#
      #bbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbb#
      #bbbbbbbb=======bbbbbbbbbbbb=======bbbbbbbb#
      #bbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbb#
      #bbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbb#
      #bbbbbbgbbbbbbbbbbbbbbbbbbbbbbbbbbbbgbbbbbb#
      #bbb=======bbbbbbbbbbbbbbbbbbbbbb=======bbb#
      .bbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbb#
      .bbbbbbbbbbgbbbbbbbbbbbbbbbbbbbbgbbbbbbbbbb#
      .bbPbbbCbb###bCbbbbbbbSbbbbbbCb###bbCbbbXbb#
      ############################################
      ############################################
      ############################################
    `),
  },
  {
    id: 'boss_arena',
    type: 'boss',
    name: 'GUARDIAN ARENA',
    tiles: grid(`
      #............................................#
      #............................................#
      #............................................#
      #............................................#
      #............................................#
      #............................................#
      #......................a.....................#
      #............................................#
      #.........a.........................a........#
      #............................................#
      #............................................#
      #............................................#
      #............................................#
      #.....................g......................#
      #...............==============...............#
      #bbb......................................bbb#
      #bbb......................................bbb#
      #bbb...g..............................g...bbb#
      #bbb========......................========bbb#
      #bbb......................................bbb#
      #bbb......................................bbb#
      #bbbP....g......g.....BB......g.....g...X.bbb#
      ##############################################
      ##############################################
    `),
  },
];

export const ROOMS_BY_ID: Readonly<Record<string, RoomTemplate>> = Object.fromEntries(ROOMS.map((r) => [r.id, r]));

/** Templates of one node type, in authoring order. */
export function roomsOfType(type: NodeType): RoomTemplate[] {
  return ROOMS.filter((r) => r.type === type);
}
